import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:3041';
const ROLE_BADGES = {
  SUPERADMIN:'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
  AVUK:'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300',
  KRYEINSPEKTORI:'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  INSPEKTORI:'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  KOMPANIA:'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
};
const ROLE_COLORS = {
  SUPERADMIN:'bg-purple-100 dark:bg-purple-900/40',
  AVUK:'bg-indigo-100 dark:bg-indigo-900/40',
  KRYEINSPEKTORI:'bg-blue-100 dark:bg-blue-900/40',
  INSPEKTORI:'bg-green-100 dark:bg-green-900/40',
  KOMPANIA:'bg-orange-100 dark:bg-orange-900/40',
};
const CREATABLE_ROLES = {
  SUPERADMIN:['SUPERADMIN','AVUK','KRYEINSPEKTORI','INSPEKTORI','KOMPANIA'],
  AVUK:['KRYEINSPEKTORI','INSPEKTORI'],
  KRYEINSPEKTORI:['INSPEKTORI'],
};
const inputCls = "w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-gray-600 transition";
const selectCls = inputCls;
const cardCls = "bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700";
const EMPTY_FORM = { username:'', email:'', first_name:'', last_name:'', password:'', role:'', region_ids:[], company_id:'', is_active:true };

function RoleBadge({ role }) {
  return <span className={`px-2 py-0.5 rounded text-xs font-semibold ${ROLE_BADGES[role]||'bg-gray-100 text-gray-700'}`}>{role}</span>;
}

export default function Users() {
  const { t } = useLanguage();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [regions, setRegions] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const authHeader = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
  const creatableRoles = CREATABLE_ROLES[currentUser?.role] || [];

  const fetchAll = useCallback(async () => {
    try {
      const [uR, rR, cR] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/users`, { headers: authHeader }),
        axios.get(`${BACKEND_URL}/api/regions`, { headers: authHeader }),
        axios.get(`${BACKEND_URL}/api/companies`, { headers: authHeader }),
      ]);
      setUsers(uR.data||[]); setRegions(rR.data||[]); setCompanies(cR.data||[]);
    } catch { toast.error('Gabim gjate ngarkimit'); }
  }, []);

  useEffect(() => { const init = async () => { setLoading(true); await fetchAll(); setLoading(false); }; init(); }, []);

  const openAdd = () => { setEditingUser(null); setForm(EMPTY_FORM); setShowForm(true); setTimeout(()=>document.getElementById('user-username-input')?.focus(),50); };
  const openEdit = (u) => {
    setEditingUser(u);
    setForm({ username:u.username||'', email:u.email||'', first_name:u.first_name||'', last_name:u.last_name||'', password:'', role:u.role||'', region_ids:u.region_ids||[], company_id:u.company_id?String(u.company_id):'', is_active:u.is_active!==false });
    setShowForm(true); setExpandedId(null); setTimeout(()=>document.getElementById('user-username-input')?.focus(),50);
  };
  const cancelForm = () => { setShowForm(false); setEditingUser(null); setForm(EMPTY_FORM); };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.username||!form.email||!form.role) { toast.error('Plotesoni fushat e detyrueshme'); return; }
    setSaving(true);
    try {
      const payload = { ...form }; if (!payload.password) delete payload.password;
      if (editingUser) { await axios.put(`${BACKEND_URL}/api/users/${editingUser.id}`, payload, { headers: authHeader }); toast.success('Perdoruesi u perditesua'); }
      else { await axios.post(`${BACKEND_URL}/api/users`, payload, { headers: authHeader }); toast.success('Perdoruesi u krijua'); }
      cancelForm(); await fetchAll();
    } catch (err) { toast.error(err.response?.data?.message||'Gabim'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`A jeni te sigurt qe doni te fshini "${u.username}"?`)) return;
    setDeletingId(u.id);
    try { await axios.delete(`${BACKEND_URL}/api/users/${u.id}`, { headers: authHeader }); toast.success('Perdoruesi u fshi'); setExpandedId(null); await fetchAll(); }
    catch { toast.error('Gabim'); } finally { setDeletingId(null); }
  };

  const toggleRegion = (id) => setForm(f => ({ ...f, region_ids: f.region_ids.includes(id) ? f.region_ids.filter(r=>r!==id) : [...f.region_ids,id] }));
  const needsRegions = ['INSPEKTORI','KRYEINSPEKTORI'].includes(form.role);
  const needsCompany = form.role === 'KOMPANIA';

  const regionNames = (ids) => {
    if (!ids?.length) return '–';
    return ids.map(id => regions.find(r=>r.id===id)?.name||id).join(', ');
  };
  const companyName = (id) => companies.find(c=>c.id===id)?.name || '–';

  return (
    <Layout>
      <div className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Menaxhimi i Perdoruesve</h1>
          {!showForm && <button onClick={openAdd} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition">+ Shto Perdorues</button>}
        </div>

        {/* ── Forma ── */}
        {showForm && (
          <div className={`${cardCls} p-5`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">{editingUser ? 'Ndrysho Perdoruesin' : 'Shto Perdorues te Ri'}</h2>
              <button type="button" onClick={cancelForm} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl leading-none">&times;</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Emri i perdoruesit *</label>
                  <input id="user-username-input" type="text" value={form.username} onChange={e=>setForm(f=>({...f,username:e.target.value}))} className={inputCls} required placeholder="username" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Email *</label>
                  <input type="email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} className={inputCls} required placeholder="email@domain.com" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{editingUser ? 'Fjalekalimi i ri (opsional)' : 'Fjalekalimi *'}</label>
                  <input type="password" value={form.password} onChange={e=>setForm(f=>({...f,password:e.target.value}))} className={inputCls} required={!editingUser} placeholder="••••••••" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Emri</label>
                  <input type="text" value={form.first_name} onChange={e=>setForm(f=>({...f,first_name:e.target.value}))} className={inputCls} placeholder="Emri" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Mbiemri</label>
                  <input type="text" value={form.last_name} onChange={e=>setForm(f=>({...f,last_name:e.target.value}))} className={inputCls} placeholder="Mbiemri" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Roli *</label>
                  <select value={form.role} onChange={e=>setForm(f=>({...f,role:e.target.value,region_ids:[],company_id:''}))} className={selectCls} required>
                    <option value="">-- Zgjidhni --</option>
                    {creatableRoles.map(r=><option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Statusi</label>
                  <select value={form.is_active?'active':'inactive'} onChange={e=>setForm(f=>({...f,is_active:e.target.value==='active'}))} className={selectCls}>
                    <option value="active">Aktiv</option>
                    <option value="inactive">Joaktiv</option>
                  </select>
                </div>
                {needsCompany && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Kompania</label>
                    <select value={form.company_id} onChange={e=>setForm(f=>({...f,company_id:e.target.value}))} className={selectCls}>
                      <option value="">-- Zgjidhni --</option>
                      {companies.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                )}
              </div>
              {needsRegions && (
                <div className="mt-4">
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Rajonet (zgjidhni nje ose me shume)</label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 border border-gray-200 dark:border-gray-600 rounded-lg p-3">
                    {regions.map(r => (
                      <label key={r.id} className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={form.region_ids.includes(r.id)} onChange={()=>toggleRegion(r.id)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                        <span className="text-xs text-gray-700 dark:text-gray-300">{r.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex gap-3 mt-5 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button type="submit" disabled={saving} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition">
                  {saving ? 'Duke ruajtur...' : (editingUser ? 'Perditeso' : 'Shto Perdorues')}
                </button>
                <button type="button" onClick={cancelForm} className="px-5 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg transition">Anulo</button>
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400 text-sm">Duke ngarkuar...</div>
        ) : users.length === 0 ? (
          <div className={`${cardCls} p-8 text-center text-gray-500 dark:text-gray-400 text-sm`}>
            Asnje perdorues. {!showForm && <button onClick={openAdd} className="text-blue-600 hover:underline">Shto perdoruesin e pare</button>}
          </div>
        ) : (
          <>
            {/* ══ MOBILE: karta me expand ══ */}
            <div className="md:hidden space-y-2">
              {users.map(u => {
                const isOpen = expandedId === u.id;
                const fullName = [u.first_name, u.last_name].filter(Boolean).join(' ') || '–';
                const active = u.is_active !== false;
                const initials = (u.first_name?.[0]||u.username?.[0]||'U').toUpperCase();
                return (
                  <div key={u.id} className={`${cardCls} overflow-hidden`}>
                    {/* Rreshti kryesor */}
                    <div className="flex items-center gap-3 px-4 py-3">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${ROLE_COLORS[u.role]||'bg-gray-100 dark:bg-gray-700'}`}>
                        <span className="text-sm font-bold text-gray-700 dark:text-gray-200">{initials}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{u.username}</p>
                        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                          <RoleBadge role={u.role} />
                          {!active && <span className="text-xs text-gray-400">· Joaktiv</span>}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setExpandedId(isOpen ? null : u.id)}
                        className={`w-8 h-8 rounded-lg flex items-center justify-center transition flex-shrink-0 ${
                          isOpen ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <circle cx="5" cy="12" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="19" cy="12" r="1.5"/>
                        </svg>
                      </button>
                    </div>

                    {/* Expand */}
                    {isOpen && (
                      <div className="border-t border-gray-100 dark:border-gray-700 px-4 py-3 space-y-2 bg-gray-50 dark:bg-gray-800/60">
                        <div className="flex justify-between">
                          <span className="text-xs text-gray-400">Emri i plotë</span>
                          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{fullName}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-xs text-gray-400">Email</span>
                          <span className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate ml-2">{u.email||'–'}</span>
                        </div>
                        {u.region_ids?.length > 0 && (
                          <div className="flex justify-between">
                            <span className="text-xs text-gray-400">Rajonet</span>
                            <span className="text-xs font-medium text-gray-700 dark:text-gray-300 text-right ml-2">{regionNames(u.region_ids)}</span>
                          </div>
                        )}
                        {u.company_id && (
                          <div className="flex justify-between">
                            <span className="text-xs text-gray-400">Kompania</span>
                            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{companyName(u.company_id)}</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-xs text-gray-400">Statusi</span>
                          <span className={`text-xs font-semibold ${active ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}>{active ? '● Aktiv' : '○ Joaktiv'}</span>
                        </div>
                        <div className="flex gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                          <button onClick={() => openEdit(u)} className="flex-1 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition">
                            ✏️ Ndrysho
                          </button>
                          <button onClick={() => handleDelete(u)} disabled={deletingId===u.id} className="flex-1 py-2 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 text-red-600 dark:text-red-400 text-sm font-medium rounded-lg hover:bg-red-100 dark:hover:bg-red-900/40 transition disabled:opacity-50">
                            {deletingId===u.id ? '...' : '🗑️ Fshi'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* ══ DESKTOP: tabela ══ */}
            <div className={`${cardCls} overflow-hidden hidden md:block`}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                    <tr>{['Emri i perdoruesit','Emri i plote','Roli','Rajonet','Kompania','Statusi','Veprimet'].map(h=>(
                      <th key={h} className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 whitespace-nowrap">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {users.map(u => (
                      <tr key={u.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700/30 transition ${editingUser?.id===u.id?'bg-blue-50 dark:bg-blue-900/10':''}`}>
                        <td className="px-3 py-2.5 font-medium text-gray-900 dark:text-white">{u.username}</td>
                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-400">{[u.first_name,u.last_name].filter(Boolean).join(' ')||'–'}</td>
                        <td className="px-3 py-2.5"><RoleBadge role={u.role} /></td>
                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-400 text-xs">{regionNames(u.region_ids)}</td>
                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-400">{companyName(u.company_id)}</td>
                        <td className="px-3 py-2.5">
                          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${u.is_active!==false?'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400':'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
                            {u.is_active!==false?'Aktiv':'Joaktiv'}
                          </span>
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex gap-2">
                            <button onClick={()=>openEdit(u)} className="px-3 py-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-xs font-medium rounded-lg transition">Ndrysho</button>
                            <button onClick={()=>handleDelete(u)} disabled={deletingId===u.id} className="px-3 py-1 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/40 text-red-600 dark:text-red-400 text-xs font-medium rounded-lg transition disabled:opacity-50">
                              {deletingId===u.id?'...':'Fshi'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
